import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PinScreen } from './PinScreen';
import { setPinApi } from '../../lib/me';

type Step = 'current' | 'new' | 'confirm';

/** Set or change the PIN. Change first verifies the current PIN, then enter the
 *  new PIN twice. Reuses the 4-digit PinScreen. */
export function PinFlow({
  mode,
  initData,
  onClose,
  onDone,
}: {
  mode: 'set' | 'change';
  initData: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>(mode === 'change' ? 'current' : 'new');
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async (pin: string, currentPin?: string) => {
    setBusy(true);
    try {
      await setPinApi(initData, currentPin ? { pin, currentPin } : { pin });
      onDone();
    } catch (err) {
      // Wrong current PIN → let them retry from the start of the change flow.
      if (mode === 'change' && String(err).includes('wrong_pin')) {
        setError(true);
        setCurrent('');
        setNext('');
        setStep('current');
      } else {
        // Anything else (e.g. endpoint not deployed / server error) — surface it
        // and exit instead of silently looping back to "enter new PIN".
        window.Telegram?.WebApp?.showAlert?.(t('profile.pinSaveFailed'));
        onClose();
      }
    } finally {
      setBusy(false);
    }
  };

  const handle = (pin: string) => {
    if (busy) return;
    setError(false);
    if (step === 'current') {
      setCurrent(pin);
      setStep('new');
    } else if (step === 'new') {
      setNext(pin);
      setStep('confirm');
    } else {
      if (pin !== next) {
        setError(true);
        setNext('');
        setStep('new');
        return;
      }
      save(pin, mode === 'change' ? current : undefined);
    }
  };

  const baseTitle =
    step === 'current'
      ? t('profile.enterCurrentPin')
      : step === 'new'
        ? t('profile.enterNewPin')
        : t('profile.confirmNewPin');
  const title = error ? `${baseTitle} · ${t('profile.pinTryAgain')}` : baseTitle;

  return <PinScreen key={`${step}-${error}`} title={title} onComplete={handle} onClose={onClose} />;
}
